import React, { useRef } from 'react';
import { Control, Controller } from 'react-hook-form';
import { Autocomplete, useJsApiLoader } from '@react-google-maps/api';

import { Input } from 'antd';
import FieldError from './FieldError';

const libraries: 'places'[] = ['places'];

export type Address = {
  streetNumber: string | null;
  streetName: string | null;
  streetType: string | null;
  suburb: string | null;
  state: string | null;
  postcode: string | null;
  country: string | null;
  formattedAddress: string | null;
} | null;

interface AddressInputProps {
  control?: Control<any>;
  name: string;
  className?: string;
  placeholder?: string;
  defaultValue?: string;
  onChange?: (value: Address) => void;
  isSelectedIndividual?: boolean;
}

const AddressInput: React.FC<AddressInputProps> = ({
  control,
  name,
  className,
  onChange,
  placeholder = '',
  isSelectedIndividual = false,
}) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY || '',
    libraries,
  });

  const inputRef = useRef<google.maps.places.Autocomplete | null>(null);

  const handleOnPlaceSelected = (field: { onChange: (value: Address) => void }) => {
    if (!inputRef.current) return;
    const place = inputRef.current.getPlace();
    if (!place || !place.address_components) return;

    const addressComponents = place.address_components || [];

    const getComponent = (type: string, useShortName = false): string =>
      addressComponents.find((comp) => comp.types.includes(type))?.[
        useShortName ? 'short_name' : 'long_name'
      ] || '';

    const streetFullName = getComponent('route') ?? null;
    const streetType = streetFullName ? (streetFullName.split(' ').pop() ?? null) : null;
    const streetName = streetType
      ? (streetFullName.replace(new RegExp(`\\s${streetType}$`), '') ?? null)
      : streetFullName;

    const parsedAddress: Address = {
      streetNumber: getComponent('street_number') ?? null,
      streetName,
      streetType,
      suburb: getComponent('locality') ?? null,
      state: getComponent('administrative_area_level_1', true) ?? null,
      postcode: getComponent('postal_code') ?? null,
      country: getComponent('country') ?? null,
      formattedAddress: place.formatted_address ?? null,
    };

    field.onChange(parsedAddress);
    onChange?.(parsedAddress);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: any,
    onChange?: (value: Address | null) => void
  ) => {
    const newValue = e.target.value;

    if (!newValue) {
      field.onChange(null);
      onChange?.(null);
      return;
    }

    field.onChange({ ...field.value, formattedAddress: newValue });
    onChange?.({ ...field.value, formattedAddress: newValue });
  };

  if (!isLoaded) return <p>Loading Google Maps...</p>;

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: 'Address is required' }}
      render={({ field, fieldState: { error } }) => (
        <Autocomplete
          onLoad={(ref) => (inputRef.current = ref)}
          onPlaceChanged={() => handleOnPlaceSelected(field)}
          options={{ componentRestrictions: { country: 'AU' } }}
        >
          <div>
            <Input
              {...field}
              value={typeof field.value === 'object' ? field.value?.formattedAddress || '' : ''}
              placeholder={placeholder}
              className={`${className} ${error ? 'border-red-500' : ''}`}
              onChange={(e) => handleChange(e, field, onChange)}
            />
            <FieldError message={isSelectedIndividual ? error?.message : undefined} />
          </div>
        </Autocomplete>
      )}
    />
  );
};

export default AddressInput;
